import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import settingsService from '../../services/settingsService';
import scheduleService from '../../services/scheduleService';
import ConfirmationModal from './ConfirmationModal';
import Toast from './Toast';

const ScheduleSettingsTailwind = () => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "info" });
  const [isEditMode, setIsEditMode] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [confirmationType, setConfirmationType] = useState('update-times');
  const [showResetSettingsConfirm, setShowResetSettingsConfirm] = useState(false);
  const [isStartingTournament, setIsStartingTournament] = useState(false);
  const [hasTournamentStarted, setHasTournamentStarted] = useState(false);
  const [showStartTournamentConfirm, setShowStartTournamentConfirm] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await settingsService.getAllSettings();
      
      if (result.success) {
        setSettings(result.data);
        if (result.fallback) {
          addToast('info', 'Verwende Standard-Einstellungen (Backend nicht verfügbar)');
        }
      } else {
        setError(result.error || 'Fehler beim Laden der Einstellungen');
        addToast('error', result.error || 'Fehler beim Laden der Einstellungen');
      }
    } catch (err) {
      console.error('Fehler beim Laden der Settings:', err);
      setError('Fehler beim Laden der Einstellungen');
      addToast('error', 'Fehler beim Laden der Einstellungen');
    } finally {
      setLoading(false);
    }
  };

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        value: value
      }
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      const settingsArray = settingsService.settingsToArray(settings);
      const result = await settingsService.updateMultipleSettings(settingsArray);
      
      if (result.success) {
        addToast('success', result.message || 'Einstellungen erfolgreich gespeichert');
        
        const hasEventsResult = await scheduleService.hasExistingEvents();
        if (hasEventsResult.success && hasEventsResult.data.hasEvents) {
          setConfirmationType('update-times');
          setShowConfirmation(true);
        }
        
        await loadSettings();
      } else {
        addToast('error', result.error || 'Fehler beim Speichern der Einstellungen');
      }
    } catch (err) {
      console.error('Fehler beim Speichern der Settings:', err);
      addToast('error', 'Fehler beim Speichern der Einstellungen');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setShowResetSettingsConfirm(true);
  };

  const performResetSettings = async () => {
    try {
      setSaving(true);
      setError(null);

      const result = await settingsService.resetSettings();
      
      if (result.success) {
        addToast('success', result.message || 'Einstellungen erfolgreich zurückgesetzt');
        await loadSettings();
        setIsEditMode(false);
      } else {
        addToast('error', result.error || 'Fehler beim Zurücksetzen der Einstellungen');
      }
    } catch (err) {
      console.error('Fehler beim Zurücksetzen der Settings:', err);
      addToast('error', 'Fehler beim Zurücksetzen der Einstellungen');
    } finally {
      setSaving(false);
    }
  };

  const handleStartTournamentClick = () => {
    setShowStartTournamentConfirm(true);
  };

  const handleConfirmStartTournament = async () => {
    setShowStartTournamentConfirm(false);
    setIsStartingTournament(true);
    try {
      const result = await scheduleService.startTournament();

      if (result.success) {
        addToast('success', result.message || 'Turnier erfolgreich gestartet');
        setHasTournamentStarted(true);
      } else {
        addToast('error', result.error || 'Fehler beim Starten des Turniers');
      }
    } catch (error) {
      console.error('Fehler beim Starten des Turniers:', error);
      addToast('error', 'Fehler beim Starten des Turniers');
    } finally {
      setIsStartingTournament(false);
    }
  };

  const addToast = (type, message) => setToast({ show: true, message, type });
  const hideToast = () => setToast(prev => ({ ...prev, show: false }));

  const handleConfirmGenerate = async () => {
    setIsGenerating(true);
    setShowConfirmation(false);
    
    try {
      let scheduleResult;
      
      if (confirmationType === 'update-times') {
        addToast('info', 'Zeiten im Zeitplan werden aktualisiert...');
        scheduleResult = await scheduleService.updateScheduleTimes();
        if (scheduleResult.success) {
          addToast('success', 'Zeiten im Zeitplan erfolgreich basierend auf neuen Einstellungen aktualisiert');
        } else {
          addToast('error', 'Fehler beim Aktualisieren der Zeiten: ' + scheduleResult.error);
        }
      } else {
        addToast('info', 'Zeitplan wird neu erstellt...');
        scheduleResult = await scheduleService.generateSchedule();
        if (scheduleResult.success) {
          addToast('success', 'Zeitplan erfolgreich basierend auf neuen Einstellungen neu erstellt');
        } else {
          addToast('error', 'Fehler beim Neu-Erstellen des Zeitplans: ' + scheduleResult.error);
        }
      }
    } catch (error) {
      const action = confirmationType === 'update-times' ? 'Aktualisieren der Zeiten' : 'Neu-Erstellen des Zeitplans';
      addToast('error', `Fehler beim ${action}`);
      console.error(`Fehler beim ${action}:`, error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCloseConfirmation = () => {
    setShowConfirmation(false);
  };

  if (loading) {
    return (
      <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header skeleton */}
          <div className="mb-6 animate-pulse">
            <div className="flex items-center gap-3 mb-1">
              <div className="h-9 bg-slate-200 dark:bg-slate-700 rounded-xl w-64"></div>
              <div className="h-6 w-12 bg-slate-200 dark:bg-slate-700 rounded-full"></div>
            </div>
            <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded-xl w-80"></div>
          </div>
          {/* Controls skeleton */}
          <div className="mb-5 flex items-center justify-between animate-pulse">
            <div className="h-9 w-32 bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
            <div className="flex gap-2">
              <div className="h-9 w-28 bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
              <div className="h-9 w-36 bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
            </div>
          </div>
          {/* Card grid skeleton */}
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm animate-pulse overflow-hidden">
                  <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100 dark:border-slate-700/60">
                    <div className="w-8 h-8 bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
                    <div className="flex-1">
                      <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-28 mb-1"></div>
                      <div className="h-3 bg-slate-100 dark:bg-slate-700/50 rounded w-40"></div>
                    </div>
                  </div>
                  <div className="p-5 space-y-4">
                    {[...Array(2)].map((_, j) => (
                      <div key={j}>
                        <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-24 mb-1.5"></div>
                        <div className="h-9 bg-slate-100 dark:bg-slate-700/50 rounded-xl w-full"></div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-2xl mb-4">
                <i className="fas fa-exclamation-triangle text-red-600 dark:text-red-400"></i>
              </div>
              <p className="text-base font-semibold text-slate-800 dark:text-slate-200 mb-1">{error}</p>
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-5">Die Einstellungen konnten nicht geladen werden.</p>
              <button
                onClick={loadSettings}
                className="inline-flex items-center gap-2 h-9 px-4 bg-primary dark:bg-primary-light text-white text-sm font-medium rounded-xl shadow-sm hover:bg-primary-dark dark:hover:bg-primary transition-colors"
              >
                <i className="fas fa-redo text-xs"></i>
                {t("admin.retry", "Erneut versuchen")}
              </button>
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="mb-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
                  {t("admin.settings.title", "Zeitplankonfiguration")}
                </h1>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-100 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-xs font-semibold text-slate-600 dark:text-slate-300">
                  <i className="fas fa-sliders-h text-[10px]"></i>
                  {Object.keys(settings).length}
                </span>
              </div>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                {t("admin.settings.subtitle", "Konfiguriere die Zeitplan-Parameter für den Wettkampf.")}
              </p>
            </div>
          </div>
        </div>

        {/* Controls Section */}
        <div className="mb-5 flex items-center justify-between gap-3">
          {/* Left: Edit toggle */}
          <button
            onClick={() => setIsEditMode((prev) => !prev)}
            className={`inline-flex items-center gap-2 h-9 px-4 text-sm font-medium rounded-xl border transition-colors ${
              isEditMode
                ? 'bg-primary dark:bg-primary-light text-white border-primary/30 dark:border-primary-light/30 shadow-sm'
                : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700'
            }`}
          >
            <i className={`fas ${isEditMode ? 'fa-lock-open' : 'fa-lock'} text-xs`}></i>
            {isEditMode ? 'Bearbeitung aktiv' : 'Bearbeiten'}
          </button>

          {/* Right: actions */}
          <div className="flex items-center gap-2">
            <button
              onClick={handleReset}
              disabled={saving}
              className="inline-flex items-center gap-2 h-9 px-3.5 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-sm font-medium rounded-xl border border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="Alle Einstellungen auf Standardwerte zurücksetzen"
            >
              <i className="fas fa-undo text-xs"></i>
              <span className="hidden sm:inline">Zurücksetzen</span>
            </button>

            <div className="h-5 w-px bg-slate-200 dark:bg-slate-700"></div>

            <button
              onClick={handleStartTournamentClick}
              disabled={isStartingTournament || hasTournamentStarted}
              className={`inline-flex items-center gap-2 h-9 px-4 text-sm font-medium rounded-xl shadow-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
                hasTournamentStarted
                  ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-700/50'
                  : 'bg-emerald-500 dark:bg-emerald-600 text-white hover:bg-emerald-600 dark:hover:bg-emerald-700'
              }`}
            >
              <i className={`fas ${isStartingTournament ? 'fa-spinner fa-spin' : hasTournamentStarted ? 'fa-check' : 'fa-flag-checkered'} text-xs`}></i>
              {hasTournamentStarted ? 'Turnier läuft' : (isStartingTournament ? 'Starte…' : 'Turnier starten')}
            </button>

            {isEditMode && (
              <button
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center gap-2 h-9 px-4 bg-primary dark:bg-primary-light text-white text-sm font-medium rounded-xl shadow-sm hover:bg-primary-dark dark:hover:bg-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <i className={`fas ${saving ? 'fa-spinner fa-spin' : 'fa-save'} text-xs`}></i>
                {saving ? 'Speichere…' : t("admin.settings.save", "Speichern")}
              </button>
            )}
          </div>
        </div>

        {/* Edit mode banner */}
        {isEditMode && (
          <div className="mb-5 px-4 py-2.5 bg-primary/5 dark:bg-primary-light/10 border border-primary/20 dark:border-primary-light/20 rounded-xl flex items-center gap-2.5">
            <i className="fas fa-pencil-alt text-primary dark:text-primary-light text-xs flex-shrink-0"></i>
            <span className="text-sm text-primary dark:text-primary-light font-medium flex-1">
              Bearbeitungsmodus aktiv – Einstellungen können geändert werden.
            </span>
            <button
              onClick={() => setIsEditMode(false)}
              className="flex-shrink-0 text-[11px] font-medium text-primary/70 dark:text-primary-light/70 hover:text-primary dark:hover:text-primary-light transition-colors"
            >
              Schließen
            </button>
          </div>
        )}

        {/* Zeitplan-Konfiguration im Kartenlayout */}
        <section className="mb-10 space-y-6">
          {/* Obere Kartenreihe */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Zeitparameter */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-visible">
              <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100 dark:border-slate-700/60">
                <div className="w-8 h-8 rounded-xl bg-sky-100 dark:bg-sky-900/30 text-sky-600 dark:text-sky-400 flex items-center justify-center flex-shrink-0">
                  <i className="fas fa-stopwatch text-xs"></i>
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-200">Zeitparameter</h2>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400">Grundparameter für die Zeitplanberechnung.</p>
                </div>
              </div>
              <div className="p-5 space-y-4">
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1.5">
                    Minuten pro Athlet
                  </label>
                  <input
                    type="number" min="1" max="10"
                    value={settings.minutes_per_athlete?.value || "2"}
                    onChange={(e) => handleSettingChange("minutes_per_athlete", e.target.value)}
                    disabled={!isEditMode || saving}
                    className="w-full px-3 h-9 border border-slate-200 dark:border-slate-600 rounded-xl text-sm text-slate-900 dark:text-slate-100 bg-slate-50 dark:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                  />
                  <p className="mt-1 text-[11px] text-slate-400 dark:text-slate-500">Dauer pro Athlet in Minuten.</p>
                </div>
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1.5">
                    Pufferzeit zwischen Gruppen <span className="normal-case font-normal">(min)</span>
                  </label>
                  <input
                    type="number" min="0" max="15"
                    value={settings.buffer_minutes?.value || "3"}
                    onChange={(e) => handleSettingChange("buffer_minutes", e.target.value)}
                    disabled={!isEditMode || saving}
                    className="w-full px-3 h-9 border border-slate-200 dark:border-slate-600 rounded-xl text-sm text-slate-900 dark:text-slate-100 bg-slate-50 dark:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                  />
                  <p className="mt-1 text-[11px] text-slate-400 dark:text-slate-500">Wartezeit zwischen zwei Gruppen.</p>
                </div>
              </div>
            </div>

            {/* Täglicher Zeitplan */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-visible">
              <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100 dark:border-slate-700/60">
                <div className="w-8 h-8 rounded-xl bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 flex items-center justify-center flex-shrink-0">
                  <i className="fas fa-calendar-day text-xs"></i>
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-200">Täglicher Zeitplan</h2>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400">Rahmenzeiten für jeden Wettkampftag.</p>
                </div>
              </div>
              <div className="p-5 space-y-4">
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1.5">
                    Startzeit
                  </label>
                  <input
                    type="time"
                    value={settings.daily_start_time?.value?.substring(0, 5) || "09:00"}
                    onChange={(e) => handleSettingChange("daily_start_time", e.target.value + ":00")}
                    disabled={!isEditMode || saving}
                    className="w-full px-3 h-9 border border-slate-200 dark:border-slate-600 rounded-xl text-sm text-slate-900 dark:text-slate-100 bg-slate-50 dark:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                  />
                  <p className="mt-1 text-[11px] text-slate-400 dark:text-slate-500">Beginn der Wettbewerbe pro Tag.</p>
                </div>
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1.5">
                    Endzeit
                  </label>
                  <input
                    type="time"
                    value={settings.daily_end_time?.value?.substring(0, 5) || "17:00"}
                    onChange={(e) => handleSettingChange("daily_end_time", e.target.value + ":00")}
                    disabled={!isEditMode || saving}
                    className="w-full px-3 h-9 border border-slate-200 dark:border-slate-600 rounded-xl text-sm text-slate-900 dark:text-slate-100 bg-slate-50 dark:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                  />
                  <p className="mt-1 text-[11px] text-slate-400 dark:text-slate-500">Geplantes Ende der Wettbewerbe.</p>
                </div>
              </div>
            </div>

            {/* Pausen */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-visible">
              <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100 dark:border-slate-700/60">
                <div className="w-8 h-8 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 flex items-center justify-center flex-shrink-0">
                  <i className="fas fa-utensils text-xs"></i>
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-200">Pausen</h2>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400">Einstellungen für die tägliche Mittagspause.</p>
                </div>
              </div>
              <div className="p-5 space-y-4">
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1.5">
                    Beginn Mittagspause
                  </label>
                  <input
                    type="time"
                    value={settings.lunch_start_time?.value?.substring(0, 5) || "12:30"}
                    onChange={(e) => handleSettingChange("lunch_start_time", e.target.value + ":00")}
                    disabled={!isEditMode || saving}
                    className="w-full px-3 h-9 border border-slate-200 dark:border-slate-600 rounded-xl text-sm text-slate-900 dark:text-slate-100 bg-slate-50 dark:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1.5">
                    Dauer <span className="normal-case font-normal">(min)</span>
                  </label>
                  <input
                    type="number" min="0" max="180"
                    value={settings.lunch_duration_minutes?.value || "60"}
                    onChange={(e) => handleSettingChange("lunch_duration_minutes", e.target.value)}
                    disabled={!isEditMode || saving}
                    className="w-full px-3 h-9 border border-slate-200 dark:border-slate-600 rounded-xl text-sm text-slate-900 dark:text-slate-100 bg-slate-50 dark:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                  />
                  {/* Status-Balken */}
                  {(() => {
                    const duration = parseInt(settings.lunch_duration_minutes?.value || "60", 10);
                    const isShort = duration < 30;
                    const isOk = duration >= 30 && duration <= 45;
                    const barColor = isShort ? "bg-red-500" : isOk ? "bg-amber-500" : "bg-emerald-500";
                    const statusText = isShort ? "Zu kurz" : isOk ? "Akzeptabel" : "Empfohlen";
                    const statusColor = isShort ? "text-red-500 dark:text-red-400" : isOk ? "text-amber-500 dark:text-amber-400" : "text-emerald-600 dark:text-emerald-400";
                    const barWidth = Math.min(100, (duration / 60) * 100);
                    return (
                      <div className="mt-2 space-y-1">
                        <div className="h-1.5 w-full rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
                          <div className={`h-full ${barColor} rounded-full transition-all`} style={{ width: `${barWidth}%` }} />
                        </div>
                        <div className="flex justify-end">
                          <span className={`text-[10px] font-semibold uppercase tracking-widest ${statusColor}`}>{statusText}</span>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>

          {/* Zweite Reihe: Wettbewerbszeitraum links, Infobox rechts */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Wettbewerbszeitraum - spannt zwei Spalten */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-visible lg:col-span-2">
              <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100 dark:border-slate-700/60">
                <div className="w-8 h-8 rounded-xl bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 flex items-center justify-center flex-shrink-0">
                  <i className="fas fa-calendar-alt text-xs"></i>
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-200">Wettbewerbszeitraum</h2>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400">Start- und Enddatum des Wettkampfs.</p>
                </div>
              </div>
              <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1.5">
                    Startdatum
                  </label>
                  <input
                    type="date"
                    value={settings.competition_start_date?.value || "2026-07-15"}
                    onChange={(e) => handleSettingChange("competition_start_date", e.target.value)}
                    disabled={!isEditMode || saving}
                    className="w-full px-3 h-9 border border-slate-200 dark:border-slate-600 rounded-xl text-sm text-slate-900 dark:text-slate-100 bg-slate-50 dark:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                  />
                  <p className="mt-1 text-[11px] text-slate-400 dark:text-slate-500">Erster Tag der Veranstaltung.</p>
                </div>
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1.5">
                    Enddatum
                  </label>
                  <input
                    type="date"
                    value={settings.competition_end_date?.value || "2026-07-17"}
                    onChange={(e) => handleSettingChange("competition_end_date", e.target.value)}
                    disabled={!isEditMode || saving}
                    className="w-full px-3 h-9 border border-slate-200 dark:border-slate-600 rounded-xl text-sm text-slate-900 dark:text-slate-100 bg-slate-50 dark:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                  />
                  <p className="mt-1 text-[11px] text-slate-400 dark:text-slate-500">Letzter Tag der Veranstaltung.</p>
                </div>
              </div>
            </div>

            {/* Info-Karte automatische Berechnung */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
              <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100 dark:border-slate-700/60">
                <div className="w-8 h-8 rounded-xl bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center flex-shrink-0">
                  <i className="fas fa-info-circle text-xs"></i>
                </div>
                <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-200">Hinweis</h3>
              </div>
              <div className="px-5 py-4">
                <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                  Alle Änderungen wirken sich unmittelbar auf die Zeitplangenerierung aus. Bitte prüfe die Kapazitäten deiner Kampfrichter, bevor du Zeiträume verkürzt oder Pausen reduzierst.
                </p>
                <div className="mt-3 flex items-center gap-1.5 text-[11px] text-blue-600 dark:text-blue-400">
                  <i className="fas fa-magic text-[10px]"></i>
                  <span>Zeitplan wird automatisch berechnet.</span>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.show}
        onClose={hideToast}
      />

      {/* Bestätigungs-Dialog für Reset der Einstellungen */}
      <ConfirmationModal
        isOpen={showResetSettingsConfirm}
        onClose={() => setShowResetSettingsConfirm(false)}
        onConfirm={performResetSettings}
        title="Einstellungen zurücksetzen"
        subtitle="Diese Aktion kann nicht rückgängig gemacht werden."
        message="Möchten Sie wirklich alle Einstellungen auf ihre Standardwerte zurücksetzen?"
        confirmText="Zurücksetzen"
        cancelText="Abbrechen"
        type="warning"
        layout="inline"
      />

      {/* Bestätigungs-Dialog für Turnierstart */}
      <ConfirmationModal
        isOpen={showStartTournamentConfirm}
        onClose={() => setShowStartTournamentConfirm(false)}
        onConfirm={handleConfirmStartTournament}
        title="Turnier starten"
        subtitle="Dieser Schritt startet das Turnier."
        message="Möchten Sie das Turnier jetzt starten? Diese Aktion kann nicht rückgängig gemacht werden."
        confirmText="Turnier starten"
        cancelText="Abbrechen"
        type="warning"
        layout="inline"
        isLoading={isStartingTournament}
      />

      <ConfirmationModal
        isOpen={showConfirmation}
        onClose={handleCloseConfirmation}
        onConfirm={handleConfirmGenerate}
        title="Zeiten im Zeitplan aktualisieren"
        message="Die Einstellungen wurden geändert. Möchten Sie die Zeiten im bestehenden Zeitplan basierend auf den neuen Einstellungen aktualisieren? Die Reihenfolge und Flächenzuordnung der Events bleibt erhalten."
        confirmText="Zeiten aktualisieren"
        cancelText="Abbrechen"
        type="info"
      />
    </main>
  );
};

export default ScheduleSettingsTailwind;
